import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import SectionHeader from '@/components/base/SectionHeader';
import { EmptyState, LoadingState } from '@/components/base/StateBlocks';
import VideoCard from '@/components/feature/VideoCard';
import FilterBar, { type SortValue } from '@/pages/category/components/FilterBar';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useSimulatedLoad } from '@/hooks/useSimulatedLoad';
import type { VideoType } from '@/types/video';
import { getHotVideos, isValidType, PAGE_SIZE, filterVideos, typeNameMap } from '@/utils/videos';

const categoryIntro: Record<VideoType, string> = {
  movie: '精选院线新片与经典佳作，动作、悬疑、科幻、爱情全覆盖，支持按地区、年份与热度筛选，快速找到今晚想看的电影。',
  tv: '汇集国产好剧与海外热播剧集，悬疑刑侦、都市情感、家庭治愈应有尽有，更新状态一目了然，追剧不再错过。',
  variety: '音综、真人秀、美食竞技与旅行纪实，轻松解压的综艺合集，按人气与更新时间排序，随时发现新节目。',
};

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const validType = isValidType(type) ? type : undefined;

  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState<SortValue>('latest');
  const [page, setPage] = useState(1);

  const { loading, retry } = useSimulatedLoad(420);

  const name = validType ? typeNameMap[validType] : '分类';

  useDocumentMeta({
    title: `${name} - 红桃视频`,
    description: validType ? categoryIntro[validType] : '红桃视频内容分类页，浏览电影、电视剧与综艺。',
    keywords: `${name},红桃视频,${name}在线观看,高清视频`,
  });

  useEffect(() => {
    setPage(1);
  }, [type, region, year, sort]);

  const list = useMemo(
    () => (validType ? filterVideos({ type: validType, region, year, sort }) : []),
    [validType, region, year, sort],
  );

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const paged = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const ranking = getHotVideos(6);

  const handlePageChange = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!validType) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <EmptyState
          title="页面不存在"
          description="该分类不存在或已调整，返回首页看看其他内容吧"
          icon="ri-error-warning-line"
        />
        <div className="flex justify-center">
          <Link
            to="/"
            className="px-5 py-2.5 rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: name }]} />

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
            {name}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600 leading-relaxed">
            {categoryIntro[validType]}
          </p>
        </div>
        <span className="text-sm text-foreground-500">
          共 <span className="text-accent-400 font-semibold">{list.length}</span> 部内容
        </span>
      </div>

      <div className="mt-6 md:hidden">
        <FilterBar
          region={region}
          year={year}
          sort={sort}
          onRegionChange={setRegion}
          onYearChange={setYear}
          onSortChange={setSort}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-8">
        <div className="min-w-0">
          {loading ? (
            <LoadingState text={`正在加载${name}内容…`} />
          ) : paged.length === 0 ? (
            <EmptyState
              title="暂无符合条件的视频"
              description="尝试更换筛选条件，或浏览其他分类"
              icon="ri-filter-off-line"
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
              {paged.map((video) => (
                <VideoCard key={video.id} video={video} showSynopsis />
              ))}
            </div>
          )}

          {!loading && paged.length > 0 && (
            <div className="mt-10">
              <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
          )}

          {!loading && paged.length === 0 && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={retry}
                className="px-5 py-2 rounded-md bg-background-200/70 text-foreground-800 text-sm whitespace-nowrap cursor-pointer hover:bg-background-300/70 transition-colors"
              >
                重新加载
              </button>
            </div>
          )}
        </div>

        <aside className="hidden lg:flex flex-col gap-5">
          <FilterBar
            region={region}
            year={year}
            sort={sort}
            onRegionChange={setRegion}
            onYearChange={setYear}
            onSortChange={setSort}
          />
          <div className="rounded-lg bg-background-100 border border-background-200/70 p-5">
            <SectionHeader title="本分类热榜" icon="ri-fire-line" />
            <ol className="flex flex-col gap-1 -mt-2">
              {ranking.map((video, i) => (
                <li key={video.id}>
                  <Link to={`/detail/${video.id}`} className="flex items-center gap-3 py-2 group">
                    <span
                      className={`w-6 h-6 shrink-0 rounded-md flex items-center justify-center text-xs font-bold ${
                        i < 3 ? 'bg-primary-500 text-background-950' : 'bg-background-200 text-foreground-600'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="text-sm text-foreground-800 line-clamp-1 group-hover:text-primary-400 transition-colors">
                      {video.title}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </div>
    </div>
  );
}